import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../store';
import { apiService } from '../../services/api';
import './TrendingAdminPanel.css';

interface AdminStatus {
  today: {
    date: string;
    data_fetched: boolean;
    ai_generated: boolean;
    total_projects: number;
    projects_with_ai: number;
  };
  recent_stats: Array<{
    date: string;
    data_fetched: boolean;
    ai_generated: boolean;
    total_projects: number;
    projects_with_ai: number;
  }>;
}

interface UpdateStatus {
  updated: boolean;
  date: string;
  last_updated: string | null;
  total_projects: number;
}

interface DataStatus {
  success: boolean;
  today: string;
  has_today_data: boolean;
  latest_data_date: string | null;
  needs_update: boolean;
  today_projects: number;
  today_ai_projects: number;
}

interface TrendingAdminPanelProps {
  onClose: () => void;
  onDataUpdate?: () => void; // 数据更新回调
}

const TrendingAdminPanel: React.FC<TrendingAdminPanelProps> = ({ onClose, onDataUpdate }) => {
  const { user } = useAppStore();
  const [adminStatus, setAdminStatus] = useState<AdminStatus | null>(null);
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus | null>(null);
  const [dataStatus, setDataStatus] = useState<DataStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '未知';
    try {
      return new Date(dateStr).toLocaleString('zh-CN');
    } catch {
      return dateStr;
    }
  };

  const loadAdminStatus = async () => {
    try {
      setLoading(true);
      
      const [adminResponse, updateResponse, dataStatusResponse] = await Promise.allSettled([
        apiService.getTrendingAdminStatus(),
        apiService.getUpdateStatus(),
        apiService.getDataStatus()
      ]);
      
      if (adminResponse.status === 'fulfilled' && adminResponse.value.success) {
        setAdminStatus(adminResponse.value);
      }
      
      if (updateResponse.status === 'fulfilled' && updateResponse.value.success) {
        setUpdateStatus(updateResponse.value.update_info);
      }

      if (dataStatusResponse.status === 'fulfilled' && dataStatusResponse.value.success) {
        setDataStatus(dataStatusResponse.value);
      }
      
    } catch (error: any) {
      console.error('获取管理员状态失败:', error);
      setMessage({ type: 'error', text: '获取状态失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleFetchTodayData = async () => {
    try {
      setFetchingData(true);
      setMessage(null);
      
      const response = await apiService.fetchTodayTrending();
      
      if (response.success) {
        setMessage({ type: 'success', text: response.message || '获取数据成功' });
        loadAdminStatus();
        // 通知父组件刷新数据
        if (onDataUpdate) {
          onDataUpdate();
        }
      } else {
        setMessage({ type: 'error', text: response.error || '获取数据失败' });
      }
    } catch (error: any) {
      console.error('获取今日数据失败:', error);
      const errorMessage = error.response?.data?.error || error.message || '获取数据失败';
      setMessage({ type: 'error', text: `获取数据失败: ${errorMessage}` });
    } finally {
      setFetchingData(false);
    }
  };

  const handleGenerateAI = async () => {
    try {
      setGeneratingAI(true);
      setMessage(null);
      
      const response = await apiService.generateAIInterpretations();
      
      if (response.success) {
        setMessage({ type: 'success', text: response.message || '生成AI解读成功' });
        loadAdminStatus();
        // 通知父组件刷新数据
        if (onDataUpdate) {
          onDataUpdate();
        }
      } else {
        setMessage({ type: 'error', text: response.error || '生成AI解读失败' });
      }
    } catch (error: any) {
      console.error('生成AI解读失败:', error);
      const errorMessage = error.response?.data?.error || error.message || '生成AI解读失败';
      setMessage({ type: 'error', text: `生成AI解读失败: ${errorMessage}` });
    } finally {
      setGeneratingAI(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) {
      loadAdminStatus();
    }
  }, [user]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  if (!user?.is_admin) {
    return null;
  }

  return (
    <div className="trending-admin-panel">
      <div className="admin-panel-header">
        <h3>
          <i className="fas fa-cog"></i>
          管理员控制面板
        </h3>
        <button className="close-btn" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="admin-panel-content">
        {loading ? (
          <div className="admin-loading">
            <i className="fas fa-spinner fa-spin"></i>
            <span>加载中...</span>
          </div>
        ) : (
          <>
            {/* 数据状态检查 */}
            {dataStatus && (
              <div className="admin-section">
                <h4><i className="fas fa-database"></i> 数据状态检查</h4>
                <div className="status-grid">
                  <div className={`status-card ${dataStatus.has_today_data ? 'success' : 'warning'}`}>
                    <div className="status-icon">
                      <i className={`fas ${dataStatus.has_today_data ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                    </div>
                    <div className="status-content">
                      <h5>{dataStatus.has_today_data ? '今日数据已获取' : '今日数据未获取'}</h5>
                      <p>今日: {dataStatus.today}</p>
                      <p>最新数据: {dataStatus.latest_data_date || '无'}</p>
                      <p>今日项目: {dataStatus.today_projects}</p>
                      <p>AI解读: {dataStatus.today_ai_projects}</p>
                      {dataStatus.needs_update && (
                        <div className="update-reminder">
                          <i className="fas fa-info-circle"></i>
                          <span>建议更新今日数据</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 更新状态 */}
            {updateStatus && (
              <div className="admin-section">
                <h4><i className="fas fa-sync-alt"></i> 数据更新状态</h4>
                <div className="status-grid">
                  <div className={`status-card ${updateStatus.updated ? 'success' : 'warning'}`}>
                    <div className="status-icon">
                      <i className={`fas ${updateStatus.updated ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                    </div>
                    <div className="status-content">
                      <h5>{updateStatus.updated ? '已更新' : '未更新'}</h5>
                      <p>日期: {updateStatus.date}</p>
                      {updateStatus.last_updated && (
                        <p>最后更新: {formatDate(updateStatus.last_updated)}</p>
                      )}
                      <p>项目数: {updateStatus.total_projects}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 今日状态 */}
            {adminStatus && (
              <div className="admin-section">
                <h4>
                  <i className="fas fa-calendar-day"></i>
                  今日详细状态
                </h4>
                
                <div className="today-status">
                  <div className="status-grid">
                    <div className="status-item">
                      <span className="status-label">数据获取</span>
                      <span className={`status-value ${adminStatus.today.data_fetched ? 'success' : 'pending'}`}>
                        <i className={`fas ${adminStatus.today.data_fetched ? 'fa-check' : 'fa-times'}`}></i>
                        {adminStatus.today.data_fetched ? '已完成' : '未完成'}
                      </span>
                    </div>
                    
                    <div className="status-item">
                      <span className="status-label">AI解读</span>
                      <span className={`status-value ${adminStatus.today.ai_generated ? 'success' : 'pending'}`}>
                        <i className={`fas ${adminStatus.today.ai_generated ? 'fa-check' : 'fa-times'}`}></i>
                        {adminStatus.today.ai_generated ? '已完成' : '未完成'}
                      </span>
                    </div>
                    
                    <div className="status-item">
                      <span className="status-label">项目数量</span>
                      <span className="status-value">
                        <i className="fas fa-star"></i>
                        {adminStatus.today.total_projects}
                      </span>
                    </div>
                    
                    <div className="status-item">
                      <span className="status-label">AI解读数</span>
                      <span className="status-value">
                        <i className="fas fa-brain"></i>
                        {adminStatus.today.projects_with_ai}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 操作按钮 */}
            <div className="admin-section">
              <h4>
                <i className="fas fa-tools"></i>
                操作
              </h4>
              
              <div className="admin-actions">
                <button
                  className="admin-btn primary"
                  onClick={handleFetchTodayData}
                  disabled={fetchingData || (adminStatus?.today.data_fetched && adminStatus.today.total_projects > 0)}
                >
                  {fetchingData ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      获取中...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-download"></i>
                      获取今日数据
                    </>
                  )}
                </button>
                
                <button
                  className="admin-btn secondary"
                  onClick={handleGenerateAI}
                  disabled={generatingAI || !adminStatus?.today.data_fetched}
                >
                  {generatingAI ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      生成中...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-brain"></i>
                      生成AI解读
                    </>
                  )}
                </button>
              </div>
              
              <div className="admin-notes">
                <p>
                  <i className="fas fa-info-circle"></i>
                  获取今日数据：从GitHub Trending API获取当日热门项目
                </p>
                <p>
                  <i className="fas fa-info-circle"></i>
                  生成AI解读：使用Qwen模型为项目生成中文解读
                </p>
              </div>
            </div>
          </>
        )}

        {/* 消息提示 */}
        {message && (
          <div className={`admin-message ${message.type}`}>
            <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
            <span>{message.text}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrendingAdminPanel;
